import { Link } from 'react-router';
import { SupportContactLink } from './LegalPage';
import { PageHeader } from './pageParts';
import { THIRD_PARTY_LICENSE_COUNTS, THIRD_PARTY_PACKAGES } from './thirdPartyNotices.generated';

interface Credit {
  id: string;
  name: string;
  use: string;
  notice: string;
  links: Array<[label: string, href: string]>;
}

/**
 * Required notices for third-party data, maps and fonts (VLP-2566 content-rights audit, sections 3 and 5).
 * Wording is attribution only; it makes no claim that any provider endorses Airview Studio.
 */
export const CREDITS: Credit[] = [
  {
    id: 'aircraft-data',
    name: 'Aircraft data: your own provider',
    use: 'Live aircraft positions, from the provider key you connect.',
    notice: 'Airview Studio supplies no aircraft data. Positions come from FlightAware AeroAPI or ADS-B Exchange using a key you provide, under that provider’s own terms, and exports of them stay subject to those terms. Practice missions use simulated data.',
    links: [['FlightAware AeroAPI', 'https://www.flightaware.com/commercial/aeroapi/'], ['ADS-B Exchange', 'https://www.adsbexchange.com/products/enterprise-api/']],
  },
  {
    id: 'osm',
    name: 'Maps: OpenFreeMap, OpenMapTiles and OpenStreetMap',
    use: 'Dark and daylight vector basemaps and offline map packs.',
    notice: 'Map styles and tiles by OpenFreeMap, schema by OpenMapTiles, data © OpenStreetMap contributors, available under the Open Database License. Map rendering by MapLibre GL JS.',
    links: [['OpenFreeMap', 'https://openfreemap.org/'], ['OpenMapTiles', 'https://openmaptiles.org/'], ['OpenStreetMap copyright', 'https://www.openstreetmap.org/copyright']],
  },
  {
    id: 'usgs-imagery',
    name: 'Imagery (US only): USGS The National Map',
    use: 'Aerial imagery layer, available in the United States only.',
    notice: 'USDA, USGS The National Map: Orthoimagery. Mostly National Agriculture Imagery Program (NAIP) photography, U.S. Government data, shown here as a convenience layer. Imagery can be several years old and is not for navigation. Outside the United States only low-resolution imagery exists, so the layer is labelled US only.',
    links: [['USGS imagery service', 'https://basemap.nationalmap.gov/arcgis/rest/services/USGSImageryOnly/MapServer'], ['USGS copyrights and credits', 'https://www.usgs.gov/information-policies-and-instructions/copyrights-and-credits']],
  },
  {
    id: 'terrain',
    name: 'Terrain: AWS Terrain Tiles',
    use: 'Terrain view, line of sight and elevation.',
    notice: 'Terrain Tiles (Terrarium) from the AWS Open Data registry, built from Mapzen/Tilezen data. Sources: USGS 3DEP and SRTM (courtesy of the U.S. Geological Survey), GMTED2010, and ETOPO1 (NOAA National Centers for Environmental Information). Outside the United States, elevation also comes from national and regional datasets, each credited by its publisher in the full attribution list linked here. Elevation is approximate and not for navigation.',
    links: [['Full terrain attribution list', 'https://github.com/tilezen/joerd/blob/master/docs/attribution.md'], ['AWS Terrain Tiles', 'https://registry.opendata.aws/terrain-tiles/']],
  },
  {
    id: 'faa-dof',
    name: 'Obstacles: FAA Digital Obstacle File',
    use: 'Towers, masts and other obstacles in the Shot Planner.',
    notice: 'FAA Digital Obstacle File (DOF), a U.S. Government publication, downloaded and cached by the Airview Studio server. Obstacles cover the United States only, include unverified entries, and may be incomplete or out of date. Heights are converted from feet. For content planning only, not for navigation or obstacle clearance.',
    links: [['FAA Digital Obstacle File', 'https://www.faa.gov/air_traffic/flight_info/aeronav/obst_data/']],
  },
  {
    id: 'osm-buildings',
    name: 'Buildings: OpenStreetMap contributors',
    use: 'Building outlines and heights in the Shot Planner.',
    notice: 'Buildings © OpenStreetMap contributors, available under the Open Database License, fetched by the Airview Studio server through the Overpass API. Heights come from OpenStreetMap tags where present and are estimated otherwise (the planner marks estimated heights). Coverage varies by area.',
    links: [['OpenStreetMap copyright', 'https://www.openstreetmap.org/copyright'], ['Overpass API', 'https://wiki.openstreetmap.org/wiki/Overpass_API']],
  },
  {
    id: 'nws-forecast',
    name: 'Forecast: National Weather Service',
    use: 'General weather and hourly forecast values (United States only).',
    notice: 'Forecast grid data from the National Weather Service (api.weather.gov), a U.S. Government work in the public domain. Values are unofficial copies for planning and are not an official forecast or briefing. Forecast values are shown for United States locations only; elsewhere no forecast is shown.',
    links: [['NWS API', 'https://www.weather.gov/documentation/services-web-api'], ['NWS disclaimer', 'https://www.weather.gov/disclaimer']],
  },
  {
    id: 'noaa',
    name: 'Weather and airspace: NOAA, NWS and FAA',
    use: 'METAR/TAF, radar and temporary flight restrictions.',
    notice: 'METAR and TAF from the NOAA Aviation Weather Center. Radar imagery from NOAA/NWS. TFR data from tfr.faa.gov, which may be incomplete or delayed. These are unofficial copies for planning and are not an official briefing; check the official source before flight. Airview Studio is not endorsed by NOAA, the NWS or the FAA.',
    links: [['Aviation Weather Center', 'https://aviationweather.gov/'], ['NWS disclaimer', 'https://www.weather.gov/disclaimer'], ['FAA TFR list', 'https://tfr.faa.gov/']],
  },
  {
    id: 'wmm',
    name: 'Magnetic declination: NOAA World Magnetic Model',
    use: 'Compass heading corrections.',
    notice: 'WMM2025 coefficients from NOAA National Centers for Environmental Information and the British Geological Survey.',
    links: [['NOAA NCEI World Magnetic Model', 'https://www.ncei.noaa.gov/products/world-magnetic-model']],
  },
  {
    id: 'ourairports',
    name: 'Airports: OurAirports',
    use: 'Airport and runway directory.',
    notice: 'Airport and runway data from OurAirports (public domain) and the NOAA Aviation Weather Center station list, provided without assurance of accuracy. Not for navigation.',
    links: [['OurAirports data', 'https://ourairports.com/data/']],
  },
  {
    id: 'cameras',
    name: 'Public cameras: Caltrans and FAA WeatherCams',
    use: 'Camera images (Caltrans) and links to the FAA viewer.',
    notice: 'Roadway camera images from Caltrans, provided as is and not for safety decisions. FAA WeatherCams open in the FAA viewer on weathercams.faa.gov; Airview Studio does not copy FAA camera images.',
    links: [['Caltrans', 'https://cwwp2.dot.ca.gov/'], ['FAA WeatherCams', 'https://weathercams.faa.gov/']],
  },
  {
    id: 'registry',
    name: 'Aircraft registration: FAA registry',
    use: 'N-number lookup.',
    notice: 'Registration details come from the FAA Releasable Aircraft Database; the archive date is shown with each record.',
    links: [['FAA registry', 'https://registry.faa.gov/']],
  },
  {
    id: 'photos',
    name: 'Aircraft photos: Wikimedia Commons',
    use: 'Aircraft photo cards.',
    notice: 'Photos are shown only when their Wikimedia Commons licence is CC BY, CC BY-SA, CC0 or public domain. Each photo shows its author, licence and a link to its file page. CC BY-SA photos are share-alike under their own licence.',
    links: [['Wikimedia Commons', 'https://commons.wikimedia.org/']],
  },
];

const FONTS: Array<[name: string, copyright: string, href: string]> = [
  ['Archivo', 'Copyright 2020 The Archivo Project Authors', 'https://github.com/Omnibus-Type/Archivo'],
  ['Chakra Petch', 'Copyright 2018 The Chakra Petch Project Authors', 'https://github.com/m4rc1e/Chakra-Petch'],
  ['JetBrains Mono', 'Copyright 2020 The JetBrains Mono Project Authors', 'https://github.com/JetBrains/JetBrainsMono'],
];

export function CreditsPage() {
  return (
    <div className="page">
      <PageHeader back={{ to: '/missions', label: 'Back to shoots' }} />
      <main className="page__main page__main--read legal-page credits-page">
        <div className="page__head">
          <div>
            <div className="eyebrow">Legal</div>
            <h1>Credits &amp; licences</h1>
            <p className="page__lede">Airview Studio is built on open data and open-source software. These are the notices that go with it.</p>
          </div>
        </div>
        <article className="prose">
          <h2>Data and maps</h2>
          {CREDITS.map((c) => (
            <section key={c.id} id={c.id} aria-labelledby={`${c.id}-h`}>
              <h3 id={`${c.id}-h`}>{c.name}</h3>
              <p className="muted small">{c.use}</p>
              <p>{c.notice}</p>
              <p>
                {c.links.map(([label, href], i) => (
                  <span key={href}>
                    {i > 0 ? ' · ' : null}
                    <a href={href} target="_blank" rel="noreferrer">{label}</a>
                  </span>
                ))}
              </p>
            </section>
          ))}
          <p className="muted small">Aircraft data from a provider you connect yourself (FlightAware AeroAPI, ADS-B Exchange) is licensed to you under that provider’s own terms.</p>

          <h2 id="fonts">Fonts</h2>
          <p>Archivo, Chakra Petch and JetBrains Mono are bundled under the SIL Open Font License, Version 1.1 (<a href="https://openfontlicense.org/open-font-license-official-text/" target="_blank" rel="noreferrer">licence text</a>). The fonts are used unmodified and are not sold separately.</p>
          <ul>
            {FONTS.map(([name, copyright, href]) => (
              <li key={name}>
                {name}: {copyright} (<a href={href} target="_blank" rel="noreferrer">source</a>)
              </li>
            ))}
          </ul>

          <h2 id="software">Open-source software</h2>
          <p>
            Airview Studio ships {THIRD_PARTY_PACKAGES.length} open-source packages in its web and server code:{' '}
            {Object.entries(THIRD_PARTY_LICENSE_COUNTS).map(([l, n]) => `${l} (${n})`).join(', ')}. None is under a copyleft licence. Each package is © its authors and is provided under its own licence without warranty.
          </p>
          <details>
            <summary>Show packages and licences</summary>
            <ul className="credits-packages">
              {THIRD_PARTY_PACKAGES.map(([name, version, license]) => (
                <li key={`${name}@${version}`}>
                  {name} {version} · {license}
                </li>
              ))}
            </ul>
          </details>
        </article>
        <nav className="legal-foot" aria-label="Legal">
          <Link to="/privacy">Privacy policy</Link>
          <Link to="/support">Support</Link>
          <SupportContactLink testId="credits-support-contact" />
          <Link to="/missions">Airview Studio</Link>
        </nav>
      </main>
    </div>
  );
}
